import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Col, Row } from 'reactstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CompletePopup from './completepopup';
import NoInternet from '../NoInternet';
import NoDataFound2 from '../NoData2';
import { useSnackbar } from "../context/SnackbarContext";

// img
import RotatePhonePopup from '../RotatePhonePopup';
import Nouser from "../../img/NoUserFound.png";
import NoNet from "../../img/NoNet.png";
import NouserDark from "../../img/NoUserFoundDark.png";
import NoNetDark from "../../img/NoNetDark.png";
import { useLanguage } from '../context/Languagecontext';
import { useTranslatedText } from '../../hooks/useTranslatedText';
import LanguageDropdown from '../Languagedropdown';
import { convertToNativeNumerals } from '../../utils/numberUtils';
import DiceButton from './DiceButton';
import BadWordWarning from './Badwordwarning';
import useBadWordDetection from '../../hooks/Usebadworddetection';
import { translateText } from '../../utils/translator';
import useCardPreview from '../../hooks/useCardPreview';

const isSmallScreen = window.innerWidth <= 375 && window.innerWidth !== 360;

// SessionStorage helper functions
const saveToSessionStorage = (key, value) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error('Error saving to sessionStorage:', error);
    }
};


// Skeleton Loader Component
const CardSkeleton = ({ width }) => (
    <div
        className="shadow rounded-4 mx-auto p-0 d-flex flex-column justify-content-between position-relative overflow-hidden"
        style={{
            border: "3px solid rgba(0, 0, 0, 0.1)",
            width: width,
            minHeight: isSmallScreen ? "400px" : "480px",
        }}
    >
        <div
            className="shimmer-element"
            style={{
                height: "90px",
                width: "100%",
            }}
        >
        </div>

        <div
            className="shimmer-element mx-auto"
            style={{
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderRadius: "50%",
                width: "120px",
                height: "120px",
            }}
        />

        {/* Comment Box Section Skeleton */}
        <div className='d-flex align-items-center justify-content-center'>
            <div
                className='mx-auto rounded-4 shimmer-element'
                style={{
                    width: "90%",
                    minHeight: "90px",
                    border: "2px solid rgba(0, 0, 0, 0.1)",
                    marginBottom: "16px"
                }}
            >
            </div>
        </div>
    </div>
);

const getInitialDarkMode = () => {
    if (typeof window !== 'undefined') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
};

const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
};

const centerimages = [
    "https://lol-image-bucket.s3.ap-south-1.amazonaws.com/images/question11/center1.png",
    "https://lol-image-bucket.s3.ap-south-1.amazonaws.com/images/question11/center2.png",
];

const HellHeavenCard = () => {
    const { lanText, t } = useLanguage();
    const location = useLocation();
    const { username, category } = useParams();
    const [img, setImg] = useState("");
    const navigate = useNavigate();

    // FIXED: Prioritize fresh data from location.state over sessionStorage
    const getStateData = () => {
        // First priority: fresh navigation data
        if (location.state && Object.keys(location.state).length > 0) {
            sessionStorage.setItem('hellheavenFlowData', JSON.stringify(location.state));
            return location.state;
        }

        // Second priority: session storage
        const sessionData = sessionStorage.getItem('hellheavenFlowData');
        if (sessionData) {
            try {
                return JSON.parse(sessionData);
            } catch (error) {
                console.error('Error parsing session data:', error);
                return null;
            }
        }

        return null;
    };

    const stateData = getStateData();

    const { hint, uploadedImage, originalData, avatar, annoyans, name } = stateData;
    const data = originalData.question;
    const questionText = Array.isArray(data)
        ? data.find(item => item.category === category)?.question || "Pick & Describe the Relationship"
        : "Pick & Describe the Relationship";
    const ques = Array.isArray(data)
        ? data.find(item => item.category === category)?.answer.heavenhellque ?? []
        : [];
    const translatedQuestion = useTranslatedText(questionText, lanText);

    // Check if success modal should be shown on initial load
    const urlParams = new URLSearchParams(window.location.search);
    const shouldShowModal = urlParams.get("success") === "1";

    const { loading, randomData, imageLoaded, noInternet, error } = useCardPreview({
        type: "heavenhell",
        category: name === "1" ? "Hell" : "Heaven",
        shouldShowModal,
    });


    const [isUploading, setIsUploading] = useState(false);
    const [isShaking, setIsShaking] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(shouldShowModal);
    const [isCheckingConnection, setIsCheckingConnection] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);
    const [noNetDarkImgSrc, setNoNetDarkImgSrc] = useState(null);
    const [noNetImgSrc, setNoNetImgSrc] = useState(null);
    const [noNetImgLoaded, setNoNetImgLoaded] = useState(false);
    const [noUserDarkImgSrc, setNoUserDarkImgSrc] = useState(null);
    const [noUserImgLoaded, setNoUserImgLoaded] = useState(false);
    const [noUserImgSrc, setNoUserImgSrc] = useState(null);
    const [textareaClicked, setTextareaClicked] = useState(false);
    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const [translatedAnnoyans, setTranslatedAnnoyans] = useState([]);
    const [comment, setComment] = useState('');
    const { hasBadWord } = useBadWordDetection(comment);
    const textareaRef = useRef(null);
    const mainContainerRef = useRef(null);
    const topRef = useRef(null);

    const { showSnackbar } = useSnackbar();

    const cardWidth = () => {
        if (window.innerWidth <= 375) return '93%';
        return '360px';
    };


    const isInAppBrowser = () => {
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        return /Instagram|FBAN|FBAV|Messenger|Telegram|FxiOS|Firefox|OPR|Opera|Brave/i.test(ua);
    };

    const checkInternetConnection = async () => {
        await new Promise(resolve => setTimeout(resolve, 500));

        if (!navigator.onLine) {
            return false;
        }

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            clearTimeout(timeoutId);
            return true;
        } catch (error) {
            console.log('Network check failed:', error);
            return false;
        }
    };
    useEffect(() => {
        if (!annoyans?.length || !t) return;
        let cancelled = false;

        const translate = async () => {
            const results = await Promise.all(
                annoyans.map(async (a) => {
                    // Support both raw format { originalQuestion, direction }
                    // and legacy format { question, answer } for session-restored data
                    const originalQ = a.originalQuestion ?? a.question ?? "";
                    const direction = a.direction;

                    const translatedQ = await translateText(originalQ, lanText);

                    let translatedA;
                    if (direction === "hell") {
                        translatedA = t?.hellheaven?.no;
                    } else if (direction === "heaven") {
                        translatedA = t?.hellheaven?.yes;
                    } else {
                        // Legacy fallback
                        translatedA = a.answer;
                    }

                    return { question: translatedQ, answer: translatedA };
                })
            );
            if (!cancelled) setTranslatedAnnoyans(results);
        };

        translate();
        return () => { cancelled = true; };
    }, [lanText, annoyans, t]);

    // Check dark mode
    useEffect(() => {
        const checkDarkMode = () => {
            const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            setIsDarkMode(darkModeMediaQuery.matches);
        };

        checkDarkMode();

        const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => setIsDarkMode(e.matches);

        darkModeMediaQuery.addEventListener('change', handleChange);

        return () => darkModeMediaQuery.removeEventListener('change', handleChange);
    }, []);

    const currentNoUserImg = isDarkMode ? noUserDarkImgSrc : noUserImgSrc;
    const currentNoNetImg = isDarkMode ? noNetDarkImgSrc : noNetImgSrc;

    // Load images from localStorage (written by CategoryLink)
    useEffect(() => {
        let isMounted = true;

        const loadImage = (key, fallbackImg, setter, setLoaded) => {
            const cached = localStorage.getItem(key);
            if (isMounted) {
                setter(cached || fallbackImg);
                if (setLoaded) setLoaded(true);
            }
        };

        loadImage('noUserImage', Nouser, setNoUserImgSrc, setNoUserImgLoaded);
        loadImage('noNetImage', NoNet, setNoNetImgSrc, setNoNetImgLoaded);
        loadImage('noUserImageDark', NouserDark, setNoUserDarkImgSrc);
        loadImage('noNetImageDark', NoNetDark, setNoNetDarkImgSrc);

        return () => { isMounted = false; }
    }, []);

    // Save location.state to sessionStorage on component mount
    useEffect(() => {
        if (location.state) {
            saveToSessionStorage('hellheavenCardState', location.state);
        }
    }, [location.state]);


    // Handle back button when modal is open
    useEffect(() => {
        const handlePopState = () => {
            if (showSuccessModal) {
                setShowSuccessModal(false);
                const url = new URL(window.location.href);
                if (url.searchParams.has("success")) {
                    url.searchParams.delete("success");
                    window.history.replaceState({}, "", url.pathname);
                }
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [showSuccessModal]);

    // Check URL params for success modal
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get("success") === "1") {
            setShowSuccessModal(true);
        }
    }, []);

    useEffect(() => {
        if (!stateData || !stateData.originalData) {
            console.log('Missing essential data, redirecting to start...');
            // navigate(`/${username}/${category}`);
        }
    }, [stateData, username, category, navigate]);

    useEffect(() => {
        const random = centerimages[Math.floor(Math.random() * centerimages.length)];
        setImg(random);
    }, []);

    useEffect(() => {
        const isDisabled = isUploading || isCheckingConnection || textareaClicked;
        window.dispatchEvent(new CustomEvent('dropdownDisabledState', {
            detail: { disabled: isDisabled }
        }));
    }, [isUploading, isCheckingConnection, textareaClicked]);

    const base64ToFile = (base64String, fileName) => {
        const arr = base64String.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], fileName, { type: mime });
    };


    const upload = async () => {
        const deviceIp = localStorage.getItem("deviceIp");
        setIsUploading(true);

        try {
            const formData = new FormData();
            const answer = translatedAnnoyans.map(item => item.answer);

            formData.append('id', originalData.id);
            formData.append('category', category);
            formData.append("webCategory", category);
            formData.append('hint', hint);

            if (uploadedImage) {
                if (uploadedImage.startsWith('data:image')) {
                    formData.append('uploadedImage', base64ToFile(uploadedImage, 'hintImage.jpg'));
                } else {
                    formData.append('uploadedImage', uploadedImage);
                }
            }
            formData.append('heavenHellBg', randomData.CardBg);
            formData.append('heavenHellBgMdlImg', img);
            formData.append('heavenHellAvatarImg', avatar);
            formData.append('heavenHellType', name === "1" ? t?.hellheaven?.hell : t?.hellheaven?.heaven);
            if (comment) {
                formData.append('heavenHellContent', comment);
            } else {
                formData.append('heavenHellQue', JSON.stringify(ques));
                formData.append('heavenHellAns', JSON.stringify(answer));
            }
            formData.append('userLocation', "allow");
            formData.append('deviceIp', deviceIp);

            await axios.post('https://api.lolcards.link/api/newinbox/create', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const url = new URL(window.location.href);
            url.searchParams.set('success', '1');
            window.history.pushState({ success: true }, '', url.toString());
            setShowSuccessModal(true);
            setComment('')
        } catch (error) {
            console.error('Error uploading data:', error);
            // const errorMessage = error.response?.data?.message || 'Something went wrong while uploading.';
            showSnackbar(t?.common?.apiFail || 'Request failed. Please try again after some time!', "error");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSendClick = async () => {
        if (!isUploading) {
            setIsCheckingConnection(true);

            const isOnline = await checkInternetConnection();

            setIsCheckingConnection(false);

            if (!isOnline) {
                showSnackbar(t?.common?.nointertoast || "Please turn on internet connection!", "error");

                return;
            }
            try {
                await upload();
                await axios.patch(
                    'https://api.lolcards.link/api/admin/monitoring',
                    { categoryname: category, id: originalData.id }
                );
            } catch (error) {
                console.error('Error updating monitoring:', error);
            }
        }
    };

    const handleModalClose = () => {
        setShowSuccessModal(false);
        // Clear all emotion flow data when successfully completed
        // sessionStorage.removeItem('emotionFlowData');
        // sessionStorage.removeItem('audioBlob');

        // Clear URL params
        const url = new URL(window.location.href);
        if (url.searchParams.has("success")) {
            url.searchParams.delete("success");
            window.history.replaceState({}, "", url.pathname);
        }
    };

    const handleKeyDown = (e) => {
        // On mobile keyboards, when user presses the "Done" button
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent default behavior (new line)

            // Blur the textarea to hide the keyboard
            e.target.blur();
        }
    };

    const maxLength = 150;
    const typedChars = [...comment].length;


    // Trigger shake animation - Added shake functionality
    const triggerShake = () => {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
    };

    const handleTextChange = (e) => {
        const newValue = e.target.value;
        const charCount = [...newValue].length;

        if (charCount > maxLength) {
            triggerShake();
            return;
        }

        setComment(newValue);

        // Update hellheavenFlowData session
        const currentData = JSON.parse(sessionStorage.getItem('hellheavenFlowData') || '{}');
        const updatedData = { ...currentData, content: newValue };
        sessionStorage.setItem('hellheavenFlowData', JSON.stringify(updatedData));
    };

    const scrollToTop = () => {
        setTimeout(() => {
            window.scrollTo(0, 0);
            if (mainContainerRef.current) mainContainerRef.current.scrollTop = 0;
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        }, 0);
    };

    // 3. Update your handleBlur function:
    const handleEditClick = () => {
        if (isUploading || isCheckingConnection) return;
        setTextareaClicked(true);

        setTimeout(() => {
            if (textareaRef.current) {
                const length = textareaRef.current.value.length;
                textareaRef.current.focus(); // prevent default scrolling
                textareaRef.current.setSelectionRange(length, length);
            }
        }, 0);
    };



    // Update handleBlur
    const handleBlur = () => {
        setKeyboardOpen(false);
        scrollToTop();
        setTextareaClicked(false);
    };

    // Add this ref at the top with other refs
    const scrollTimeoutRef = useRef(null);

    const handleFocus = (e) => {
        setKeyboardOpen(true);
        // Prevent default scroll behavior
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        // Clear any pending scroll operations
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        // Scroll to top immediately to prevent keyboard push
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;

        // Then scroll to topRef smoothly after a brief delay
        scrollTimeoutRef.current = setTimeout(() => {
            requestAnimationFrame(() => {
                if (topRef.current) {
                    const element = topRef.current;
                    const rect = element.getBoundingClientRect();
                    const scrollTop = window.scrollY || document.documentElement.scrollTop;
                    const targetY = rect.top + scrollTop;

                    window.scrollTo({
                        top: targetY,
                        behavior: "smooth"
                    });
                }
            });
        }, 100);
    };

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (noInternet) return <NoInternet style={{ height: '100vh' }} t={t.noData} Nouser={currentNoNetImg} imgLoaded={noNetImgLoaded} />;
    if (error) return <NoDataFound2 style={{ height: '100vh' }} t={t.noData2} Nouser={currentNoUserImg} username={username} category={category} imgLoaded={noUserImgLoaded} />;


    return (
        <div
            style={{
                height: "100dvh",
                backgroundColor: loading || !imageLoaded ? (isDarkMode ? "#1a1a1a" : "#ffffff") : undefined,
                backgroundImage: !loading && imageLoaded ? `url(${randomData.CardBg})` : "none",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* BLUR OVERLAY */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: loading || !imageLoaded
                        ? (isDarkMode ? "rgba(26, 26, 26, 0.95)" : "rgba(255, 255, 255, 0.95)")
                        : "rgba(0, 0, 0, 0.15)",
                    backdropFilter: "blur(11px)",
                    WebkitBackdropFilter: "blur(11px)",
                    zIndex: 1,
                }}
            ></div>

            <div className="h-100 pt-3" style={{ position: "relative", zIndex: 2 }}>
                <div
                    className={`mx-auto justify-content-evenly h-100`}
                    style={{
                        width: "400px",
                        maxWidth: "95vw",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {textareaClicked && (
                        <div ref={topRef}
                            className="d-flex justify-content-center align-items-center mt-2"
                            style={{ height: '70px' }}
                        >
                            <div className="text-center" style={{ maxWidth: "60vw" }}>
                                <p
                                    className="lh-sm mx-auto ps-2 m-0"
                                    style={{
                                        fontSize: "clamp(15px, 4.5vw, 17px)",
                                        color: "#fff"
                                    }}
                                >
                                    {t?.hellheaven?.cardTitle || "Share your thoughts on"} {name === "1" ? t?.hellheaven?.hell : t?.hellheaven?.heaven}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* STEP 1 - Top Section */}
                    <div
                        className={`d-flex flex-column flex-grow-1 ${isIOS ? "my-2" : "my-2"}`}
                        style={{
                            justifyContent: textareaClicked ? "start" : "center",
                            alignContent: textareaClicked ? "end" : "center",
                            transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {/* Show skeleton while loading or image not loaded */}
                        {loading || !imageLoaded ? (
                            <CardSkeleton width={cardWidth()} />
                        ) : (
                            <>
                                <div
                                    className={`rounded-4 mx-auto p-0 d-flex flex-column overflow-hidden ${showSuccessModal ? "" : "card-enter"}`}
                                    style={{
                                        width: cardWidth(),
                                        minHeight: textareaClicked ? "240px" : isSmallScreen ? "430px" : "480px",
                                        boxShadow: "0px 6.44px 8.59px 0px rgba(0, 0, 0, 0.25)",
                                        background: textareaClicked ? "rgba(255, 255, 255, 0.1)" : ``,
                                        transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                                    }}
                                >
                                    <div
                                        className={`rounded-4 mx-auto p-0 d-flex flex-column overflow-hidden ${textareaClicked && isShaking ? "shake-animation" : ""}`}
                                        style={{
                                            border: textareaClicked ? "1px solid rgba(255, 255, 255, 0.4)" : "3px solid black",
                                            width: "100%",
                                            minHeight: textareaClicked ? "240px" : isSmallScreen ? "430px" : "480px",
                                            backgroundImage: textareaClicked ? "" : `url(${randomData.CardBg})`,
                                            backgroundRepeat: "no-repeat",
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            boxShadow: "0px 6.44px 8.59px 0px rgba(0, 0, 0, 0.25)",
                                            position: 'relative',
                                            justifyContent: textareaClicked ? "start" : "space-between",
                                            transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                                        }}
                                    >
                                        <div className='position-relative mx-auto'
                                            style={{
                                                width: textareaClicked ? "100%" : "95%",
                                                minHeight: textareaClicked ? "70px" : "110px",
                                                background: textareaClicked ? "white" : "transparent"
                                            }}
                                        >
                                            {!textareaClicked && (
                                                <svg className='mt-2'
                                                    viewBox="0 0 295 111"
                                                    preserveAspectRatio="none"
                                                    style={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        width: "100%",
                                                        height: "100%",
                                                        zIndex: 0,
                                                    }}
                                                    fill="none"
                                                >
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M80.0614 97.5768C100.665 102.083 101.535 97.0238 110.359 93.0408C114.479 98.2792 121.689 102.028 129.098 102.911C140.419 104.26 146.018 101.52 153.142 98.2753C158.353 101.997 158.93 104.147 166.878 107.101C182.619 112.952 208.201 107.517 213.042 90.2853C215.305 90.8493 217.016 91.6005 219.361 91.9953C225.752 93.0709 230.8 92.0052 236.946 90.0455C246.985 86.8449 248.763 81.362 251.391 78.3331C262.946 81.9556 275.081 81.6078 284.237 74.8165C284.358 74.727 284.531 74.6038 284.649 74.5124L290.211 68.2531C302.692 45.8132 277.544 24.1255 249.418 34.2883C240.909 9.21831 205.523 9.41778 194.023 17.8333C191.31 16.6663 183.506 7.858 168.446 5.84103C154.499 3.97295 146.189 6.89731 136.654 10.9461C133.771 9.65339 130.964 7.3321 127.212 5.52648C123.798 3.88349 120.025 2.53059 115.462 1.67182C105.963 -0.115578 95.7135 0.77935 87.9782 3.61533C71.4002 9.69257 63.7985 20.4595 62.4859 35.4639C43.2994 32.5822 28.3585 40.4692 27.8078 54.7667C-10.727 51.0658 -7.96644 98.9733 35.2225 89.6486C39.6497 105.688 63.9132 110.037 80.0614 97.5768Z" fill="white" />
                                                    <path d="M80.0614 97.5768C100.665 102.083 101.535 97.0238 110.359 93.0408C114.479 98.2792 121.689 102.028 129.098 102.911C140.419 104.26 146.018 101.52 153.142 98.2753C158.353 101.997 158.93 104.147 166.878 107.101C182.619 112.952 208.201 107.517 213.042 90.2853C215.305 90.8493 217.016 91.6005 219.361 91.9953C225.752 93.0709 230.8 92.0052 236.946 90.0454C246.985 86.8449 248.763 81.362 251.391 78.3331C262.946 81.9556 275.081 81.6078 284.237 74.8165C284.358 74.727 284.531 74.6038 284.649 74.5124L290.211 68.2531C302.692 45.8132 277.544 24.1255 249.418 34.2883C240.909 9.21831 205.523 9.41777 194.023 17.8333C191.31 16.6663 183.506 7.858 168.446 5.84103C154.499 3.97294 146.189 6.89731 136.654 10.9461C133.771 9.65339 130.964 7.3321 127.212 5.52648C123.798 3.88349 120.025 2.53059 115.462 1.67182C105.963 -0.115578 95.7135 0.779347 87.9782 3.61533C71.4002 9.69257 63.7985 20.4595 62.4859 35.4639C43.2994 32.5822 28.3585 40.4692 27.8078 54.7667C-10.727 51.0658 -7.96644 98.9733 35.2225 89.6486C39.6497 105.688 63.9132 110.037 80.0614 97.5768Z" stroke="#231F20" stroke-width="1.5001" stroke-miterlimit="2.61313" />
                                                </svg>
                                            )}

                                            <span
                                                className={`text-black position-absolute ${textareaClicked ? "GTMedium text-start" : "bricolage-grotesque text-center px-2"
                                                    }`}
                                                style={{
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    // fontSize: textareaClicked ? "20px" : "18px",
                                                    wordBreak: "break-word",

                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    fontSize: textareaClicked ? "20px" :
                                                        translatedQuestion.length > 60
                                                            ? "clamp(12px, 2vw, 14px)"
                                                            : translatedQuestion.length > 40
                                                                ? "clamp(15px, 2.2vw, 16px)"
                                                                : "clamp(16px, 2.5vw, 18px)",
                                                    width: "78%",
                                                    lineHeight: "16px",
                                                    fontWeight: "500",
                                                    paddingLeft: textareaClicked ? "60px" : "",
                                                    paddingTop: textareaClicked ? "" : "20px"
                                                }}
                                            >
                                                {textareaClicked ? (
                                                    name === "1" ? t?.hellheaven?.hell : t?.hellheaven?.heaven
                                                ) : (
                                                    <>
                                                        {t?.common?.que || 'Que:'} {translatedQuestion}
                                                    </>
                                                )}                                            </span>

                                        </div>

                                        <div
                                            className=' d-flex align-items-end justify-content-center mx-auto'
                                            style={{
                                                position: textareaClicked ? "absolute" : "relative",
                                                top: textareaClicked ? "35px" : "0",
                                                left: textareaClicked ? "40px" : "0",
                                                margin: textareaClicked ? "" : "5px auto 13px",
                                                transform: textareaClicked ? "translate(-50%, -50%)" : "",
                                                background: textareaClicked ? "#f2f2f2" : "",
                                                border: textareaClicked ? "2px solid #353435" : "",
                                                width: textareaClicked ? "55px" : isSmallScreen ? "95px" : "115px",
                                                height: textareaClicked ? "55px" : isSmallScreen ? "95px" : "115px",
                                                transition: "all 0s cubic-bezier(0.4, 0, 0.2, 1)",
                                                overflow: "hidden",
                                                borderRadius: "50%"
                                            }}
                                        >
                                            <img
                                                src={avatar}
                                                alt={name === "1" ? t?.hellheaven?.hell : t?.hellheaven?.heaven}
                                                style={{
                                                    width: textareaClicked ? "90%" : "80%",
                                                    height: textareaClicked ? "90%" : "80%",
                                                    objectFit: "contain",
                                                    marginBottom: textareaClicked ? "0" : "8px",
                                                    zIndex: 3
                                                }}
                                            />

                                            {!textareaClicked && (
                                                <img
                                                    src={img}
                                                    alt="top icon"
                                                    style={{
                                                        position: "absolute",
                                                        top: "0px",
                                                        right: "0px",
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "contain",
                                                        zIndex: 2
                                                    }}
                                                />
                                            )}
                                        </div>


                                        {textareaClicked ? (
                                            <div className='p-2 fw-semibold text-start d-flex flex-column GTRegular' style={{ height: "100%" }}>
                                                <textarea
                                                    ref={textareaRef}
                                                    placeholder={`${t?.emotion?.cardPlaceholder || "Write your throught"}`}
                                                    value={comment}
                                                    onKeyDown={handleKeyDown}
                                                    onChange={handleTextChange}
                                                    onFocus={handleFocus}
                                                    onBlur={handleBlur}
                                                    className='border-0 flex-grow-1 custom-placeholder-9'
                                                    style={{
                                                        resize: 'none', backgroundColor: 'transparent',
                                                        outline: 'none', boxShadow: 'none', color: 'white',
                                                        fontSize: '17px', fontWeight: '400', lineHeight: '1.5',
                                                        caretColor: "white",
                                                    }}
                                                    enterKeyHint="done"
                                                    inputMode="text"
                                                    autoFocus
                                                />

                                                <DiceButton
                                                    category={category}
                                                    // subCategory={name === "1" ? 'Hell' : 'Heaven'}
                                                    onContentFetched={(text) => setComment(text)}
                                                    disabled={isUploading || isCheckingConnection}
                                                />
                                            </div>
                                        ) : (
                                            // ✅ SHOW THIS WHEN textareaClicked = false
                                            <>
                                                <div className="d-flex mt-2 align-items-center justify-content-center position-relative p-2 bg-white mb-3 mx-auto rounded-4"
                                                    style={{ minHeight: "110px", width: "90%", border: "2.5px solid black" }}>
                                                    <>
                                                        {comment ? (
                                                            <Row className="w-100 position-relative align-items-center h-100" style={{ padding: isSmallScreen ? "12px" : "16px" }}>
                                                                <Col
                                                                    xs={12}
                                                                    onClick={handleEditClick}
                                                                    className="pt-3 pe-3 cursor bricolage-grotesque"
                                                                    style={{
                                                                        fontSize: '18px',
                                                                        fontWeight: "700",
                                                                        height: 'auto',
                                                                        wordBreak: 'break-all',
                                                                        overflowWrap: 'anywhere',
                                                                        whiteSpace: 'pre-wrap',
                                                                        lineHeight: "19px",
                                                                        textAlign: 'center',
                                                                    }}
                                                                >
                                                                    {comment}
                                                                </Col>

                                                                <button
                                                                    onClick={handleEditClick}
                                                                    disabled={isUploading || isCheckingConnection}
                                                                    className="btn btn-link p-0 position-absolute"
                                                                    style={{
                                                                        bottom: "0px",
                                                                        right: "0px",
                                                                        fontSize: '18px',
                                                                        color: '#666',
                                                                        cursor: isUploading || isCheckingConnection ? 'not-allowed' : 'pointer',// ✅ ADDED
                                                                        opacity: "1"
                                                                    }}
                                                                >
                                                                    <div
                                                                        className='rounded-circle overflow-hidden d-flex justify-content-center align-items-center ms-auto'
                                                                        style={{ width: 28, height: 28, background: "#F3F3F3", border: "1px solid black" }}
                                                                    >
                                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M0.281899 10.0906L0.00598075 12.3747C-0.00693942 12.4719 0.00112984 12.5708 0.0296453 12.6647C0.0581607 12.7586 0.10646 12.8452 0.17129 12.9189C0.236121 12.9925 0.315977 13.0514 0.40548 13.0916C0.494984 13.1317 0.592056 13.1523 0.69016 13.1518C0.719384 13.1518 0.748581 13.15 0.777567 13.1463L3.06164 12.8704C3.54182 12.8095 3.98802 12.5904 4.32971 12.2476L10.2481 6.32914C10.2995 6.27776 10.3283 6.20808 10.3283 6.13542C10.3283 6.06277 10.2995 5.99309 10.2481 5.9417L7.21165 2.90249C7.16027 2.85112 7.09059 2.82227 7.01793 2.82227C6.94528 2.82227 6.8756 2.85112 6.82421 2.90249L0.904703 8.82255C0.561886 9.16424 0.342737 9.61044 0.281899 10.0906Z" fill="black" />
                                                                            <path d="M12.7366 1.83298L11.3199 0.416277C11.188 0.284306 11.0314 0.179617 10.859 0.10819C10.6866 0.0367636 10.5019 0 10.3153 0C10.1287 0 9.94396 0.0367636 9.77159 0.10819C9.59923 0.179617 9.44262 0.284306 9.31073 0.416277L7.98062 1.74638C7.92925 1.79777 7.90039 1.86746 7.90039 1.94012C7.90039 2.01278 7.92925 2.08247 7.98062 2.13385L11.019 5.17222C11.0704 5.22359 11.1401 5.25245 11.2127 5.25245C11.2854 5.25245 11.3551 5.22359 11.4065 5.17222L12.7366 3.84211C12.8685 3.71022 12.9732 3.55361 13.0447 3.38125C13.1161 3.20888 13.1528 3.02412 13.1528 2.83754C13.1528 2.65096 13.1161 2.46621 13.0447 2.29384C12.9732 2.12147 12.8685 1.96487 12.7366 1.83298Z" fill="black" />
                                                                            <path d="M13.1519 12.6046C13.1519 12.4593 13.0942 12.3199 12.9914 12.2171C12.8886 12.1144 12.7493 12.0566 12.6039 12.0566H6.30191C6.15657 12.0566 6.01718 12.1144 5.91441 12.2171C5.81164 12.3199 5.75391 12.4593 5.75391 12.6046C5.75391 12.75 5.81164 12.8894 5.91441 12.9921C6.01718 13.0949 6.15657 13.1526 6.30191 13.1526H12.6039C12.7493 13.1526 12.8886 13.0949 12.9914 12.9921C13.0942 12.8894 13.1519 12.75 13.1519 12.6046Z" fill="black" />
                                                                        </svg>

                                                                    </div>
                                                                </button>
                                                            </Row>
                                                        ) : (
                                                            <div className="w-100 position-relative h-100 d-flex align-items-center pt-3">
                                                                <table
                                                                    className="table table-borderless mb-0"
                                                                    style={{ fontSize: "16px", width: "94%" }}
                                                                >
                                                                    <tbody>
                                                                        {translatedAnnoyans?.map((value, index) => {
                                                                            const isLast = index === translatedAnnoyans.length - 1;
                                                                            const rowClass = isLast ? "p-0" : "p-0 pb-2";

                                                                            return (
                                                                                <tr key={index}>
                                                                                    <td
                                                                                        className={`text-center bricolage-grotesque pe-2 ${rowClass}`}
                                                                                        style={{
                                                                                            width: "73%",
                                                                                            color: "#373435",
                                                                                            verticalAlign: "middle",
                                                                                            background: "transparent",
                                                                                            lineHeight: "18px"
                                                                                        }}
                                                                                    >
                                                                                        {capitalizeFirstLetter(value.question)}
                                                                                    </td>

                                                                                    <td
                                                                                        className={`text-center bricolage-grotesque ${rowClass}`}
                                                                                        style={{
                                                                                            width: "1%",
                                                                                            color: "#373435",
                                                                                            verticalAlign: "middle",
                                                                                            background: "transparent",
                                                                                        }}
                                                                                    >
                                                                                        :
                                                                                    </td>

                                                                                    <td
                                                                                        className={`text-center ps-2 bricolage-grotesque ${rowClass}`}
                                                                                        style={{
                                                                                            width: "26%",
                                                                                            color: "#373435",
                                                                                            verticalAlign: "middle",
                                                                                            background: "transparent",
                                                                                        }}
                                                                                    >
                                                                                        {capitalizeFirstLetter(value.answer)}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                                <button
                                                                    onClick={handleEditClick}
                                                                    disabled={isUploading || isCheckingConnection}
                                                                    className="btn btn-link p-0 position-absolute"
                                                                    style={{
                                                                        bottom: "0px",
                                                                        right: "0px",
                                                                        fontSize: '18px',
                                                                        color: '#666',
                                                                        cursor: isUploading || isCheckingConnection ? 'not-allowed' : 'pointer',// ✅ ADDED
                                                                        opacity: "1"
                                                                    }}
                                                                >
                                                                    <div
                                                                        className='rounded-circle overflow-hidden d-flex justify-content-center align-items-center ms-auto'
                                                                        style={{ width: 25, height: 25, background: "#F3F3F3", border: "1px solid black" }}
                                                                    >
                                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M0.281899 10.0906L0.00598075 12.3747C-0.00693942 12.4719 0.00112984 12.5708 0.0296453 12.6647C0.0581607 12.7586 0.10646 12.8452 0.17129 12.9189C0.236121 12.9925 0.315977 13.0514 0.40548 13.0916C0.494984 13.1317 0.592056 13.1523 0.69016 13.1518C0.719384 13.1518 0.748581 13.15 0.777567 13.1463L3.06164 12.8704C3.54182 12.8095 3.98802 12.5904 4.32971 12.2476L10.2481 6.32914C10.2995 6.27776 10.3283 6.20808 10.3283 6.13542C10.3283 6.06277 10.2995 5.99309 10.2481 5.9417L7.21165 2.90249C7.16027 2.85112 7.09059 2.82227 7.01793 2.82227C6.94528 2.82227 6.8756 2.85112 6.82421 2.90249L0.904703 8.82255C0.561886 9.16424 0.342737 9.61044 0.281899 10.0906Z" fill="black" />
                                                                            <path d="M12.7366 1.83298L11.3199 0.416277C11.188 0.284306 11.0314 0.179617 10.859 0.10819C10.6866 0.0367636 10.5019 0 10.3153 0C10.1287 0 9.94396 0.0367636 9.77159 0.10819C9.59923 0.179617 9.44262 0.284306 9.31073 0.416277L7.98062 1.74638C7.92925 1.79777 7.90039 1.86746 7.90039 1.94012C7.90039 2.01278 7.92925 2.08247 7.98062 2.13385L11.019 5.17222C11.0704 5.22359 11.1401 5.25245 11.2127 5.25245C11.2854 5.25245 11.3551 5.22359 11.4065 5.17222L12.7366 3.84211C12.8685 3.71022 12.9732 3.55361 13.0447 3.38125C13.1161 3.20888 13.1528 3.02412 13.1528 2.83754C13.1528 2.65096 13.1161 2.46621 13.0447 2.29384C12.9732 2.12147 12.8685 1.96487 12.7366 1.83298Z" fill="black" />
                                                                            <path d="M13.1519 12.6046C13.1519 12.4593 13.0942 12.3199 12.9914 12.2171C12.8886 12.1144 12.7493 12.0566 12.6039 12.0566H6.30191C6.15657 12.0566 6.01718 12.1144 5.91441 12.2171C5.81164 12.3199 5.75391 12.4593 5.75391 12.6046C5.75391 12.75 5.81164 12.8894 5.91441 12.9921C6.01718 13.0949 6.15657 13.1526 6.30191 13.1526H12.6039C12.7493 13.1526 12.8886 13.0949 12.9914 12.9921C13.0942 12.8894 13.1519 12.75 13.1519 12.6046Z" fill="black" />
                                                                        </svg>

                                                                    </div>
                                                                </button>
                                                            </div>
                                                        )}
                                                        <div
                                                            className="position-absolute"
                                                            style={{
                                                                top: "-21px",
                                                                left: "50%",
                                                                transform: "translateX(-50%)",
                                                                display: "inline-block"   // 🔥 important
                                                            }}
                                                        >
                                                            <div
                                                                className="position-relative d-inline-flex align-items-center justify-content-center px-4"
                                                                style={{
                                                                    fontWeight: "700",
                                                                    fontSize: "18px",
                                                                    height: "40px",
                                                                    minWidth: "150px",
                                                                    whiteSpace: "nowrap"
                                                                }}
                                                            >
                                                                {/* SVG Background */}
                                                                <svg
                                                                    viewBox="0 0 142 37"
                                                                    preserveAspectRatio="none"
                                                                    style={{
                                                                        position: "absolute",
                                                                        inset: 0,
                                                                        width: "100%",
                                                                        height: "100%",
                                                                        zIndex: 0
                                                                    }}
                                                                    fill="none"
                                                                >
                                                                    <path d="M142 18.4995L138.86 19.4743C137.268 19.9683 136.14 22.3243 134.833 25.0488C133.606 27.6083 132.215 30.51 130.029 32.8306C127.638 35.3689 124.744 36.7245 121.204 36.9613V36.9955H120.42C120.302 36.9982 120.18 37 120.057 37V36.9955H21.9425V37C21.8236 37 21.7053 37 21.5877 36.9955H21.5697C17.6755 36.9137 14.5341 35.5515 11.9722 32.8306C9.78589 30.51 8.3952 27.6083 7.16815 25.0488C5.86127 22.323 4.73327 19.9686 3.1411 19.4749L0 18.4995L3.1395 17.5247C4.73183 17.031 5.85958 14.6747 7.16621 11.9505C8.39376 9.39171 9.78302 6.49 11.9708 4.16938C14.5343 1.44874 17.6752 0.0863304 21.5711 0.00451705C21.6926 0.00177223 21.8169 1.1573e-06 21.9427 1.1573e-06H120.057C120.182 1.1573e-06 120.307 1.37871e-06 120.43 0.00451705C124.324 0.0863304 127.466 1.44847 130.027 4.16938C132.216 6.49 133.606 9.39171 134.833 11.9535C136.14 14.6792 137.268 17.0335 138.86 17.5274L142 18.4995Z" fill="white" />
                                                                    <path d="M138.86 17.5269C137.268 17.0332 136.14 14.6769 134.833 11.953C133.606 9.3934 132.215 6.49177 130.029 4.17115C127.467 1.45042 124.325 0.0881003 120.432 0.00628693C120.308 0.00354211 120.184 0.00177104 120.059 0.00177104H21.9425C21.8166 0.00177104 21.6925 0.00177126 21.5707 0.00628693C17.6748 0.0881003 14.5353 1.45042 11.9703 4.17115C9.78327 6.49177 8.39376 9.3934 7.16621 11.9553C5.85966 14.682 4.73183 17.0357 3.13975 17.5297L0 18.5012L3.13975 19.4749C4.73183 19.9687 5.85966 22.3248 7.16671 25.0487C8.39376 27.6086 9.78429 30.5102 11.9708 32.8306C14.5344 35.5515 17.6752 36.9137 21.568 36.9957H21.586C21.7037 36.9982 21.8219 37 21.9408 37V36.9957H120.057V37C120.179 37 120.301 37 120.42 36.9957H121.204V36.9613C124.745 36.7244 127.638 35.3681 130.029 32.8306C132.216 30.5102 133.606 27.6086 134.834 25.0487C136.14 22.3223 137.268 19.9682 138.86 19.4743L142 18.4995L138.86 17.5269ZM133.321 24.0679C130.907 29.1057 128.171 34.8154 120.401 34.9815H21.5983C13.831 34.8154 11.0942 29.1034 8.67997 24.0679C7.5841 21.7811 6.59734 19.7228 5.24614 18.5012C6.59734 17.2817 7.5841 15.2233 8.67997 12.9348C11.0947 7.89579 13.8321 2.18461 21.6066 2.02045C21.7178 2.01771 21.831 2.01718 21.9447 2.01718H120.057C120.17 2.01718 120.283 2.01718 120.397 2.02045C128.169 2.18461 130.906 7.89579 133.321 12.9348C134.417 15.2216 135.404 17.28 136.755 18.5012C135.404 19.721 134.417 21.7801 133.321 24.0679Z" fill="#040404" />
                                                                </svg>

                                                                {/* Text */}
                                                                <span className='bricolage-grotesque'
                                                                    style={{
                                                                        position: "relative",
                                                                        zIndex: 1,
                                                                        fontWeight: "700",
                                                                        lineHeight: "15px",
                                                                        fontSize: '17px',
                                                                    }}>
                                                                    {name === "1" ? t?.hellheaven?.hell : t?.hellheaven?.heaven}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {textareaClicked && (
                                    <>
                                        <p className={`text-end text-white mt-2 pe-4 GTMedium mb-0`}>
                                            {convertToNativeNumerals(typedChars, lanText)}/{convertToNativeNumerals(maxLength, lanText)}
                                        </p>

                                        <BadWordWarning
                                            visible={hasBadWord}
                                            message={
                                                t?.common?.badWordWarning ||
                                                "Avoid using offensive language"
                                            }
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    {!textareaClicked && (
                        <div className='mx-auto mt-auto mb-2' style={{ width: "90%" }}>
                            {loading || !imageLoaded ? (
                                // Button Skeleton
                                <div
                                    className="rounded-pill mx-auto shimmer-element"
                                    style={{
                                        width: "100%",
                                        height: "55px",
                                        transform: "scale(0.95)",
                                    }}
                                />
                            ) : (
                                <Button
                                    className="rounded-pill mx-auto GTMedium text-white border-0"
                                    style={{
                                        width: "100%",
                                        padding: "14px 1px",
                                        height: "56px",
                                        cursor: (!isUploading) ? 'pointer' : 'not-allowed',
                                        background: "black",
                                        transform: "scale(0.95)",
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease',
                                    }}
                                    onClick={handleSendClick}
                                    disabled={isUploading || isCheckingConnection}
                                >
                                    {isUploading || isCheckingConnection ? (
                                        <>
                                            <div className="custom-spinner" style={{ borderTopColor: "#fff" }} />
                                        </>
                                    ) : (
                                        <span className='GTMedium'
                                            style={{
                                                fontSize: "clamp(21px, 4.5vw, 27px)",
                                                lineHeight: 1,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {t?.annoyCard.send || "Send"}
                                        </span>
                                    )}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>


            <RotatePhonePopup
                excludeWhen={[keyboardOpen]}
            />
            <LanguageDropdown />

            <CompletePopup
                showSuccessModal={showSuccessModal}
                toggle={handleModalClose}
                data={originalData}
                username={username}
                category={category}
                style={{ zIndex: 10000 }}
            />
        </div >
    );
};

export default HellHeavenCard;